import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounced } from '@/hooks/useDebounced';
import { Building2, Copy, MapPin, MessageSquareText, Pencil, Plus, Trash2, UserRound } from 'lucide-react';
import { CUSTOMER_TYPES, CUSTOMER_TYPE_LABELS, formatDate } from '@erp/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader, SearchInput, ErrorState, EmptyState } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { DataTable, Pagination, type Column } from '@/components/ui/table';
import { CityField, CountrySelect, PostalCodeField, ProvinceSelect } from '@/components/ui/geo-fields';
import { useEditLock } from '@/hooks/useEditLock';
import { EditLockBanner } from '@/components/ui/edit-lock-banner';
import { InlineView } from '@/components/ui/inline-view';
import { Field, Input, Select, Textarea } from '@/components/ui/input';
import { toast } from 'sonner';

interface ShippingAddress {
  id: string; label: string; recipient: string | null; phone: string | null;
  address: string; city: string; province: string | null;
  postal_code: string; country: string; notes: string | null; is_default: boolean;
  country_code: string | null; region_code: string | null;
}

interface CustomerRow {
  id: string; code: string; type: string; name: string; trade_name: string | null;
  vat_number: string | null; email: string | null; phone: string | null;
  address: string | null; city: string | null; province: string | null; postal_code: string | null;
  country: string; notes: string | null; order_count: number; shipping_count: number;
  country_code: string | null; region_code: string | null;
  default_shipping: string | null;
}

interface Contact {
  id: string; name: string; role: string | null; email: string | null;
  phone: string | null; isPrimary: boolean; notes: string | null;
}

interface Note {
  id: string; kind: string; body: string; created_at: string; author_name: string | null;
}

interface PaymentMethod { id: string; name: string; days: number; requiresIban: boolean; active?: boolean }

interface BillingSnapshot {
  address: string | null; city: string | null; province: string | null;
  postalCode: string | null; country: string | null;
}

interface CustomerDetail extends Omit<CustomerRow, 'notes'> {
  /** Historial de seguimiento (la ficha libre del cliente va en `CustomerRow.notes`). */
  addresses: ShippingAddress[];
  contacts: Contact[];
  notes_history: Note[];
  paymentMethod: { id: string; name: string; days: number } | null;
  billing_effective: BillingSnapshot;
  billing_same_as_fiscal: boolean;
  billing_address: string | null; billing_city: string | null; billing_province: string | null;
  billing_postal_code: string | null; billing_country: string | null;
  billing_country_code: string | null; billing_region_code: string | null;
  vat_regime: string; default_discount_pct: string; iban: string | null;
}

const NOTE_KINDS: { value: string; label: string }[] = [
  { value: 'note', label: 'Nota' },
  { value: 'call', label: 'Llamada' },
  { value: 'visit', label: 'Visita' },
  { value: 'incident', label: 'Incidencia' },
  { value: 'claim', label: 'Reclamación' }
];

const VAT_REGIME_LABELS: Record<string, string> = {
  general: 'Régimen general',
  intracomunitario: 'Intracomunitario (UE)',
  exportacion: 'Exportación',
  canarias_ceuta_melilla: 'Canarias, Ceuta o Melilla'
};

interface AddressDraft {
  id?: string; label: string; recipient: string; phone: string; address: string;
  city: string; province: string; postalCode: string; country: string; notes: string; isDefault: boolean;
  /** Códigos del catálogo (ISO del país y provincia); el texto de arriba se conserva. */
  countryCode: string; regionCode: string;
}

const EMPTY_FORM = {
  code: '', type: 'company', name: '', tradeName: '', vatNumber: '', email: '', phone: '',
  address: '', city: '', province: '', postalCode: '', country: 'ES', notes: '',
  countryCode: 'ES', regionCode: '',
  billingSameAsFiscal: true, billingAddress: '', billingCity: '', billingProvince: '',
  billingPostalCode: '', billingCountry: 'ES', billingCountryCode: 'ES', billingRegionCode: '',
  vatRegime: 'general',
  defaultDiscountPct: '0', paymentMethodId: '', iban: ''
};

const emptyAddress = (isDefault = false): AddressDraft => ({
  label: '', recipient: '', phone: '', address: '', city: '', province: '',
  postalCode: '', country: 'ES', notes: '', isDefault, countryCode: 'ES', regionCode: ''
});

export function CustomersPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const busquedaDebounced = useDebounced(search, 300);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CustomerRow | null>(null);

  const customersQuery = useQuery({
    queryKey: ['customers-page', busquedaDebounced, page],
    queryFn: () => api<{ data: CustomerRow[]; meta: { page: number; pageSize: number; total: number; totalPages: number } }>(
      `/customers?page=${page}${busquedaDebounced ? `&q=${encodeURIComponent(busquedaDebounced)}` : ''}`
    ),
    // Al cambiar de página se mantienen los datos anteriores: sin esto la tabla se vacía, el paginador
    // desaparece y el contador dice «0 clientes» durante la carga.
    placeholderData: keepPreviousData
  });

  const rows = customersQuery.data?.data ?? [];
  const meta = customersQuery.data?.meta;

  // Si al archivar (o borrar) el último de la página se queda vacía, se vuelve a la anterior: si no, la
  // pantalla muestra «0» y el paginador desaparece sin forma de salir.
  useEffect(() => {
    if (customersQuery.isSuccess && !customersQuery.isFetching && page > 1 && (customersQuery.data?.data.length ?? 0) === 0) setPage(page - 1);
  }, [customersQuery.isSuccess, customersQuery.isFetching, customersQuery.data, page]);

  const columns: Column<CustomerRow>[] = [
    {
      key: 'name',
      header: 'Cliente',
      cell: (row) => (
        <div className="min-w-[230px]">
          <p className="font-medium">{row.name}</p>
          {row.trade_name ? <p className="text-[11px] text-muted-foreground">{row.trade_name}</p> : null}
          <p className="font-mono text-[10px] text-muted-foreground">{row.code}{row.vat_number ? ` · ${row.vat_number}` : ''}</p>
        </div>
      )
    },
    {
      key: 'type',
      header: 'Tipo',
      cell: (row) => (
        <Badge tone={row.type === 'freelancer' ? 'accent' : 'info'}>
          {row.type === 'freelancer' ? <UserRound className="size-3" /> : <Building2 className="size-3" />}
          {CUSTOMER_TYPE_LABELS[row.type as keyof typeof CUSTOMER_TYPE_LABELS] ?? row.type}
        </Badge>
      )
    },
    { key: 'contact', header: 'Contacto', cell: (row) => <div className="text-[11px] text-muted-foreground"><p>{row.email ?? '—'}</p><p>{row.phone ?? ''}</p></div> },
    { key: 'city', header: 'Facturación', cell: (row) => <div className="text-[11px]"><p>{row.city ?? '—'}</p><p className="text-muted-foreground">{row.province ?? ''} {row.postal_code ?? ''}</p></div> },
    {
      key: 'shipping',
      header: 'Envíos',
      cell: (row) => (
        <div>
          <Badge tone="outline"><MapPin className="size-3" /> {row.shipping_count}</Badge>
          {row.default_shipping ? <p className="mt-1 max-w-[190px] truncate text-[10px] text-muted-foreground">{row.default_shipping}</p> : null}
        </div>
      )
    },
    { key: 'orders', header: 'Pedidos', cell: (row) => <Badge tone="outline">{row.order_count}</Badge> },
    {
      key: 'actions',
      header: '',
      cell: (row) => can('orders.write') ? <Button variant="ghost" size="icon-sm" onClick={() => { setEditing(row); setFormOpen(true); }}><Pencil /></Button> : null
    }
  ];

  return (
    <div>
      <div className={formOpen ? 'hidden' : ''}>
      <PageHeader
        title="Clientes"
        description="Empresas y autónomos, dirección de facturación y múltiples direcciones de envío"
        actions={can('orders.write') ? <Button onClick={() => { setEditing(null); setFormOpen(true); }}><Plus /> Nuevo cliente</Button> : null}
      />
      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <SearchInput value={search} onChange={(valor) => { setSearch(valor); setPage(1); }} placeholder="Buscar por nombre, nombre comercial, CIF o código…" className="w-full sm:w-80" />
          <div className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground"><Building2 className="size-3" /> {meta?.total ?? rows.length} clientes</div>
        </div>
        {customersQuery.error ? <ErrorState message={(customersQuery.error as Error).message} onRetry={() => void customersQuery.refetch()} /> : (
          <>
            <DataTable columns={columns} rows={rows} loading={customersQuery.isLoading} rowKey={(row) => row.id}
              empty={<EmptyState title="Sin clientes" description="Crea el primer cliente para poder emitir pedidos." icon={Building2} />} />
            {meta && meta.totalPages > 1 ? (
              <Pagination page={meta.page} totalPages={meta.totalPages} total={meta.total} onPageChange={setPage} />
            ) : null}
          </>
        )}
      </Card>
      </div>

      <CustomerModal
        open={formOpen} onOpenChange={setFormOpen} customer={editing}
        onSaved={() => {
          setFormOpen(false); setEditing(null);
          void qc.invalidateQueries({ queryKey: ['customers-page'] });
          // El selector del alta de pedidos usa `customers-options`; la clave antigua ya no la escucha nadie.
    void qc.invalidateQueries({ queryKey: ['customers-options'] });
        }}
      />
    </div>
  );
}

function CustomerModal({ open, onOpenChange, customer, onSaved }: {
  open: boolean; onOpenChange: (open: boolean) => void; customer: CustomerRow | null; onSaved: () => void;
}) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [form, setForm] = useState({ ...EMPTY_FORM });
  // Un cliente nuevo no existe todavía: no hay nada que bloquear hasta que se guarde.
  const lock = useEditLock('customer', open && customer ? customer.id : null);
  const soloLectura = Boolean(customer) && !lock.canEdit;
  const [addresses, setAddresses] = useState<AddressDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ['customer-detail', customer?.id],
    queryFn: () => api<CustomerDetail>(`/customers/${customer!.id}`),
    enabled: open && Boolean(customer?.id)
  });
  const methodsQuery = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () => api<{ data: PaymentMethod[] }>('/payment-methods'),
    enabled: open
  });
  const methods = methodsQuery.data?.data ?? [];
  const [newContact, setNewContact] = useState({ name: '', role: '', email: '', phone: '' });
  const [newNote, setNewNote] = useState({ kind: 'note', body: '' });

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (!customer) {
      setForm({ ...EMPTY_FORM });
      setAddresses([]);
      return;
    }
    const source = detailQuery.data;
    setForm({
      code: source?.code ?? customer.code,
      type: source?.type ?? customer.type,
      name: source?.name ?? customer.name,
      tradeName: source?.trade_name ?? customer.trade_name ?? '',
      vatNumber: source?.vat_number ?? customer.vat_number ?? '',
      email: source?.email ?? customer.email ?? '',
      phone: source?.phone ?? customer.phone ?? '',
      address: source?.address ?? customer.address ?? '',
      city: source?.city ?? customer.city ?? '',
      province: source?.province ?? customer.province ?? '',
      postalCode: source?.postal_code ?? customer.postal_code ?? '',
      country: source?.country ?? customer.country,
      countryCode: source?.country_code ?? customer.country_code ?? '',
      regionCode: source?.region_code ?? '',
      notes: customer.notes ?? '',
      billingSameAsFiscal: source?.billing_same_as_fiscal ?? true,
      billingAddress: source?.billing_address ?? '',
      billingCity: source?.billing_city ?? '',
      billingProvince: source?.billing_province ?? '',
      billingPostalCode: source?.billing_postal_code ?? '',
      billingCountry: source?.billing_country ?? source?.country ?? 'ES',
      billingCountryCode: source?.billing_country_code ?? source?.country_code ?? '',
      billingRegionCode: source?.billing_region_code ?? '',
      vatRegime: source?.vat_regime ?? 'general',
      defaultDiscountPct: source?.default_discount_pct ?? '0',
      paymentMethodId: source?.paymentMethod?.id ?? '',
      iban: source?.iban ?? ''
    });
    if (source) {
      setAddresses(source.addresses.map((address) => ({
        id: address.id, label: address.label, recipient: address.recipient ?? '',
        phone: address.phone ?? '', address: address.address, city: address.city,
        province: address.province ?? '', postalCode: address.postal_code,
        country: address.country, notes: address.notes ?? '', isDefault: address.is_default,
        countryCode: address.country_code ?? '', regionCode: address.region_code ?? ''
      })));
    }
  }, [open, customer, detailQuery.data]);

  const update = (key: keyof typeof form, value: string) => setForm((current) => ({ ...current, [key]: value }));

  const refreshDetail = async () => {
    await detailQuery.refetch();
    void qc.invalidateQueries({ queryKey: ['customers-page'] });
  };

  const addContact = async () => {
    if (!customer) return;
    if (!newContact.name.trim()) { toast.error('El contacto necesita nombre'); return; }
    try {
      await api(`/customers/${customer.id}/contacts`, { method: 'POST', body: {
        name: newContact.name, role: newContact.role || null, email: newContact.email || null,
        phone: newContact.phone || null, isPrimary: (detailQuery.data?.contacts.length ?? 0) === 0
      } });
      setNewContact({ name: '', role: '', email: '', phone: '' });
      await refreshDetail();
      toast.success('Contacto añadido');
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const removeContact = async (contactId: string) => {
    if (!customer) return;
    try {
      await api(`/customers/${customer.id}/contacts/${contactId}`, { method: 'DELETE' });
      await refreshDetail();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const addNote = async () => {
    if (!customer) return;
    if (!newNote.body.trim()) { toast.error('La nota está vacía'); return; }
    try {
      await api(`/customers/${customer.id}/notes`, { method: 'POST', body: {
        kind: newNote.kind, body: newNote.body
      } });
      setNewNote({ kind: 'note', body: '' });
      await refreshDetail();
      toast.success('Nota guardada');
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const removeNote = async (noteId: string) => {
    if (!customer) return;
    try {
      await api(`/customers/${customer.id}/notes/${noteId}`, { method: 'DELETE' });
      await refreshDetail();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const updateAddress = (index: number, key: keyof AddressDraft, value: string | boolean) => {
    setAddresses((current) => current.map((address, position) => position === index ? { ...address, [key]: value } : address));
  };

  const setDefaultAddress = (index: number) => {
    setAddresses((current) => current.map((address, position) => ({ ...address, isDefault: position === index })));
  };

  const removeAddress = (index: number) => {
    setAddresses((current) => {
      const next = current.filter((_, position) => position !== index);
      if (next.length && !next.some((address) => address.isDefault)) next[0] = { ...next[0]!, isDefault: true };
      return next;
    });
  };

  const addAddress = () => {
    setAddresses((current) => [...current, emptyAddress(current.length === 0)]);
  };

  const copyBillingAddress = () => {
    if (!form.address.trim() || !form.city.trim() || !form.postalCode.trim()) {
      toast.error('Completa la dirección de facturación antes de copiarla');
      return;
    }
    setAddresses((current) => [...current, {
      label: current.length === 0 ? 'Domicilio fiscal' : `Copia facturación ${current.length + 1}`,
      recipient: form.name, phone: form.phone, address: form.address, city: form.city,
      province: form.province, postalCode: form.postalCode, country: form.country || 'ES',
      notes: '', isDefault: current.length === 0,
      countryCode: form.countryCode || '', regionCode: form.regionCode
    }]);
  };

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      if (!form.name.trim()) throw new Error('El nombre o razón social es obligatorio');
      if (form.iban.trim() && !/^[A-Za-z]{2}\d{2}[A-Za-z0-9]{11,30}$/.test(form.iban.replace(/[\s-]/g, ''))) {
        throw new Error('El IBAN no tiene un formato válido (dos letras, dos dígitos y entre 11 y 30 caracteres)');
      }
      if (addresses.some((address) => !address.label.trim() || !address.address.trim() || !address.city.trim() || !address.postalCode.trim())) {
        throw new Error('Cada dirección de envío necesita etiqueta, dirección, ciudad y código postal');
      }
      const payload = {
        code: form.code || undefined,
        type: form.type,
        name: form.name,
        tradeName: form.tradeName || null,
        vatNumber: form.vatNumber || null,
        email: form.email || null,
        phone: form.phone || null,
        address: form.address || null,
        city: form.city || null,
        province: form.province || null,
        postalCode: form.postalCode || null,
        country: form.country || 'ES',
        countryCode: form.countryCode || null,
        regionCode: form.regionCode || null,
        notes: form.notes || null,
        billingSameAsFiscal: form.billingSameAsFiscal,
        ...(form.billingSameAsFiscal ? {} : {
          billingAddress: form.billingAddress || null,
          billingCity: form.billingCity || null,
          billingProvince: form.billingProvince || null,
          billingPostalCode: form.billingPostalCode || null,
          billingCountry: form.billingCountry || 'ES',
          billingCountryCode: form.billingCountryCode || null,
          billingRegionCode: form.billingRegionCode || null
        }),
        vatRegime: form.vatRegime,
        defaultDiscountPct: Number(form.defaultDiscountPct || 0),
        paymentMethodId: form.paymentMethodId || null,
        iban: form.iban || null,
        addresses: addresses.map(({ id, ...address }) => ({
          ...(id ? { id } : {}),
          label: address.label,
          recipient: address.recipient || null,
          phone: address.phone || null,
          address: address.address,
          city: address.city,
          province: address.province || null,
          postalCode: address.postalCode,
          country: address.country || 'ES',
          countryCode: address.countryCode || null,
          regionCode: address.regionCode || null,
          notes: address.notes || null,
          isDefault: address.isDefault
        }))
      };
      if (customer) await api(`/customers/${customer.id}`, { method: 'PATCH', body: payload });
      else await api('/customers', { method: 'POST', body: payload });
      toast.success('Cliente guardado');
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const loadingDetail = Boolean(customer) && detailQuery.isLoading;

  return (
    <InlineView
      open={open} onOpenChange={onOpenChange} size="xl"
      title={customer ? `Editar ${customer.name}` : 'Nuevo cliente'}
      description="Empresa o autónomo · facturación y direcciones de envío independientes"
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button><Button loading={saving} disabled={loadingDetail || soloLectura} onClick={() => void submit()}>Guardar cliente</Button></>}
    >
      <div className="space-y-5">
        {/* Fuera del fieldset: sus botones tienen que seguir activos en solo lectura. */}
        <EditLockBanner
          state={lock.state}
          onTakeOver={() => void lock.takeOver()}
          onRetry={() => void lock.retry()}
        />
        <fieldset disabled={soloLectura} className="m-0 min-w-0 space-y-5 border-0 p-0">
        {error ? <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</div> : null}
        {loadingDetail ? <div className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">Cargando direcciones del cliente…</div> : null}

        <section>
          <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Building2 className="size-3.5" /> Identificación fiscal</h3>
          <div className="grid gap-3 sm:grid-cols-4">
            <Field label="Tipo de cliente" required>
              <Select value={form.type} onChange={(e) => update('type', e.target.value)}>
                {CUSTOMER_TYPES.map((type) => <option key={type} value={type}>{CUSTOMER_TYPE_LABELS[type]}</option>)}
              </Select>
            </Field>
            <Field label={form.type === 'freelancer' ? 'Nombre y apellidos' : 'Razón social'} required className="sm:col-span-2">
              <Input value={form.name} onChange={(e) => update('name', e.target.value)} />
            </Field>
            <Field label="Código" hint="Automático si se deja vacío"><Input value={form.code} onChange={(e) => update('code', e.target.value)} disabled={Boolean(customer)} /></Field>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <Field label={form.type === 'freelancer' ? 'Nombre comercial' : 'Nombre comercial / marca'}><Input value={form.tradeName} onChange={(e) => update('tradeName', e.target.value)} /></Field>
            <Field label={form.type === 'freelancer' ? 'NIF' : 'CIF / VAT'}><Input value={form.vatNumber} onChange={(e) => update('vatNumber', e.target.value)} /></Field>
            <Field label="Teléfono"><Input value={form.phone} onChange={(e) => update('phone', e.target.value)} /></Field>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field label="Email"><Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} /></Field>
            <Field label="Notas"><Input value={form.notes} onChange={(e) => update('notes', e.target.value)} /></Field>
          </div>
        </section>

        <section className="rounded-lg border border-border bg-muted/20 p-3">
          <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><MapPin className="size-3.5" /> Domicilio fiscal</h3>
          <div className="grid gap-3 sm:grid-cols-4">
            <Field label="Dirección" className="sm:col-span-2"><Input value={form.address} onChange={(e) => update('address', e.target.value)} /></Field>
            <Field label="Ciudad">
              <CityField
                countryCode={form.countryCode} value={form.city} regionCode={form.regionCode}
                onChange={(texto) => update('city', texto)}
                onPickProvince={(code, nombre) => setForm((f) => ({ ...f, regionCode: code, province: nombre }))}
              />
            </Field>
            <Field label="Provincia">
              <ProvinceSelect
                countryCode={form.countryCode} value={form.regionCode} textValue={form.province}
                onChange={(code, nombre) => setForm((f) => ({ ...f, regionCode: code, province: nombre }))}
              />
            </Field>
            <Field label="Código postal">
              <PostalCodeField
                countryCode={form.countryCode} value={form.postalCode} city={form.city}
                onChange={(texto) => update('postalCode', texto)}
                onApply={(cambios) => setForm((f) => ({ ...f, ...cambios }))}
              />
            </Field>
            <Field label="País">
              <CountrySelect
                value={form.countryCode}
                onChange={(code) => setForm((f) => ({
                  ...f, countryCode: code, country: code, regionCode: '', province: ''
                }))}
              />
            </Field>
          </div>
        </section>

        <section className="rounded-lg border border-border bg-muted/20 p-3">
          <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><MapPin className="size-3.5" /> Facturación y pago</h3>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={form.billingSameAsFiscal}
              onChange={(e) => setForm((current) => (e.target.checked ? {
                ...current, billingSameAsFiscal: true
              } : {
                ...current, billingSameAsFiscal: false,
                // Se copia lo fiscal para que se vea lo que se va a guardar, no un hueco vacío.
                billingAddress: current.billingAddress || current.address,
                billingCity: current.billingCity || current.city,
                billingProvince: current.billingProvince || current.province,
                billingPostalCode: current.billingPostalCode || current.postalCode,
                billingCountry: current.billingCountry || current.country || 'ES'
              }))}
            />
            Facturar a la misma dirección que el domicilio fiscal
          </label>
          {!form.billingSameAsFiscal ? (
            <div className="mt-3 grid gap-3 sm:grid-cols-4">
              <Field label="Dirección de facturación" className="sm:col-span-2"><Input value={form.billingAddress} onChange={(e) => update('billingAddress', e.target.value)} /></Field>
              <Field label="Ciudad">
                <CityField
                  countryCode={form.billingCountryCode} value={form.billingCity}
                  regionCode={form.billingRegionCode}
                  onChange={(texto) => update('billingCity', texto)}
                  onPickProvince={(code, nombre) => setForm((f) => ({
                    ...f, billingRegionCode: code, billingProvince: nombre
                  }))}
                />
              </Field>
              <Field label="Provincia">
                <ProvinceSelect
                  countryCode={form.billingCountryCode} value={form.billingRegionCode} textValue={form.billingProvince}
                  onChange={(code, nombre) => setForm((f) => ({ ...f, billingRegionCode: code, billingProvince: nombre }))}
                />
              </Field>
              <Field label="Código postal">
                <PostalCodeField
                  countryCode={form.billingCountryCode} value={form.billingPostalCode}
                  city={form.billingCity}
                  onChange={(texto) => update('billingPostalCode', texto)}
                  onApply={(cambios) => setForm((f) => ({
                    ...f,
                    ...(cambios.city !== undefined ? { billingCity: cambios.city } : {}),
                    ...(cambios.regionCode !== undefined ? { billingRegionCode: cambios.regionCode } : {}),
                    ...(cambios.province !== undefined ? { billingProvince: cambios.province } : {})
                  }))}
                />
              </Field>
              <Field label="País">
                <CountrySelect
                  value={form.billingCountryCode}
                  onChange={(code) => setForm((f) => ({
                    ...f, billingCountryCode: code, billingCountry: code, billingRegionCode: '', billingProvince: ''
                  }))}
                />
              </Field>
            </div>
          ) : null}
          <div className="mt-3 grid gap-3 sm:grid-cols-4">
            <Field label="Régimen de IVA">
              <Select value={form.vatRegime} onChange={(e) => update('vatRegime', e.target.value)}>
                {Object.entries(VAT_REGIME_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </Select>
            </Field>
            <Field label="Forma de pago" hint="El pedido nuevo la hereda">
              <Select value={form.paymentMethodId} onChange={(e) => update('paymentMethodId', e.target.value)}>
                <option value="">Sin definir</option>
                {methods.map((method) => (
                  <option key={method.id} value={method.id}>
                    {method.name}{method.days ? ` · ${method.days} días` : ''}{method.active === false ? ' (desactivada)' : ''}
                  </option>
                ))}
                {/* Si la que tiene el cliente ya no está activa, se muestra igual: sin esto el
                    desplegable caería en «Sin definir» y guardar la cambiaría sin querer. */}
                {detailQuery.data?.paymentMethod && !methods.some((method) => method.id === detailQuery.data!.paymentMethod!.id) ? (
                  <option value={detailQuery.data.paymentMethod.id}>
                    {detailQuery.data.paymentMethod.name} (desactivada)
                  </option>
                ) : null}
              </Select>
            </Field>
            <Field label="Descuento por defecto (%)"><Input type="number" min={0} max={100} step="0.5" value={form.defaultDiscountPct} onChange={(e) => update('defaultDiscountPct', e.target.value)} /></Field>
            <Field label="IBAN" hint="Obligatorio si la forma de pago es domiciliada">
              <Input value={form.iban} onChange={(e) => update('iban', e.target.value)} placeholder="ES00 0000 0000 0000 0000 0000" />
            </Field>
          </div>
        </section>

        <section>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><UserRound className="size-3.5" /> Direcciones de envío</h3>
              <p className="mt-1 text-[11px] text-muted-foreground">Puedes añadir varias. La marcada como predeterminada se propone al crear un pedido.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={copyBillingAddress}><Copy /> Copiar facturación</Button>
              <Button variant="outline" size="sm" onClick={addAddress}><Plus /> Añadir dirección</Button>
            </div>
          </div>

          {addresses.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-xs text-muted-foreground">
              Sin direcciones de envío. Si no añades ninguna, el pedido usará la dirección de facturación.
            </div>
          ) : (
            <div className="space-y-3">
              {addresses.map((address, index) => (
                <div key={address.id ?? `new-${index}`} className="rounded-lg border border-border p-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold">Dirección {index + 1}</span>
                      <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <input type="checkbox" checked={address.isDefault} onChange={() => setDefaultAddress(index)} />
                        Predeterminada
                      </label>
                    </div>
                    <Button variant="ghost" size="icon-sm" onClick={() => removeAddress(index)} title="Eliminar"><Trash2 /></Button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-4">
                    <Field label="Etiqueta" required className="sm:col-span-2"><Input value={address.label} onChange={(e) => updateAddress(index, 'label', e.target.value)} placeholder="Almacén central / Hotel Benidorm…" /></Field>
                    <Field label="Persona de contacto"><Input value={address.recipient} onChange={(e) => updateAddress(index, 'recipient', e.target.value)} /></Field>
                    <Field label="Teléfono"><Input value={address.phone} onChange={(e) => updateAddress(index, 'phone', e.target.value)} /></Field>
                    <Field label="Dirección" required className="sm:col-span-2"><Input value={address.address} onChange={(e) => updateAddress(index, 'address', e.target.value)} /></Field>
                    <Field label="Ciudad" required>
                      <CityField
                        countryCode={address.countryCode} value={address.city} regionCode={address.regionCode}
                        onChange={(texto) => updateAddress(index, 'city', texto)}
                        onPickProvince={(code, nombre) => setAddresses((current) => current.map((item, i) =>
                          i === index ? { ...item, regionCode: code, province: nombre } : item))}
                      />
                    </Field>
                    <Field label="Provincia">
                      <ProvinceSelect
                        countryCode={address.countryCode} value={address.regionCode} textValue={address.province}
                        onChange={(code, nombre) => setAddresses((current) => current.map((item, i) =>
                          i === index ? { ...item, regionCode: code, province: nombre } : item))}
                      />
                    </Field>
                    <Field label="Código postal" required>
                      <PostalCodeField
                        countryCode={address.countryCode} value={address.postalCode} city={address.city}
                        onChange={(texto) => updateAddress(index, 'postalCode', texto)}
                        onApply={(cambios) => setAddresses((filas) => filas.map((fila, i) => (
                          i === index ? { ...fila, ...cambios } : fila
                        )))}
                      />
                    </Field>
                    <Field label="País">
                      <CountrySelect
                        value={address.countryCode}
                        onChange={(code) => setAddresses((current) => current.map((item, i) =>
                          i === index
                            ? { ...item, countryCode: code, country: code, regionCode: '', province: '' }
                            : item))}
                      />
                    </Field>
                    <Field label="Notas" className="sm:col-span-2"><Textarea value={address.notes} onChange={(e) => updateAddress(index, 'notes', e.target.value)} rows={2} /></Field>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        </fieldset>
        {/* Contactos y seguimiento van **fuera** del bloqueo a propósito: apuntar una llamada o
            quitar un contacto no puede esperar a quien está editando las direcciones, y sus rutas
            no viajan en el PATCH de la ficha. */}
        {customer ? (
          <section>
            <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><UserRound className="size-3.5" /> Contactos</h3>
            <p className="mb-2 text-[11px] text-muted-foreground">Las personas con las que se habla: compras, administración, recepción de mercancía…</p>
            <div className="space-y-2">
              {(detailQuery.data?.contacts ?? []).map((contact) => (
                <div key={contact.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-xs font-medium">
                      {contact.name}
                      {contact.isPrimary ? <Badge tone="info" className="ml-2">Principal</Badge> : null}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {[contact.role, contact.email, contact.phone].filter(Boolean).join(' · ') || 'Sin datos de contacto'}
                    </p>
                  </div>
                  {can('orders.write') ? (
                    <Button variant="ghost" size="icon-sm" title="Quitar" onClick={() => void removeContact(contact.id)}><Trash2 /></Button>
                  ) : null}
                </div>
              ))}
              {(detailQuery.data?.contacts ?? []).length === 0 ? (
                <p className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-[11px] text-muted-foreground">Sin contactos todavía.</p>
              ) : null}
            </div>
            {can('orders.write') ? (
              <div className="mt-3 grid gap-2 sm:grid-cols-5">
                <Input placeholder="Nombre" value={newContact.name} onChange={(e) => setNewContact((c) => ({ ...c, name: e.target.value }))} />
                <Input placeholder="Cargo" value={newContact.role} onChange={(e) => setNewContact((c) => ({ ...c, role: e.target.value }))} />
                <Input placeholder="Email" value={newContact.email} onChange={(e) => setNewContact((c) => ({ ...c, email: e.target.value }))} />
                <Input placeholder="Teléfono" value={newContact.phone} onChange={(e) => setNewContact((c) => ({ ...c, phone: e.target.value }))} />
                <Button variant="outline" onClick={() => void addContact()}><Plus /> Añadir contacto</Button>
              </div>
            ) : null}
          </section>
        ) : null}

        {customer ? (
          <section>
            <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><MessageSquareText className="size-3.5" /> Seguimiento</h3>
            <p className="mb-2 text-[11px] text-muted-foreground">Historial de lo hablado con el cliente, con autor y fecha. No genera avisos.</p>
            {can('orders.write') ? (
              <div className="mb-3 space-y-2">
                <div className="flex flex-wrap gap-2">
                  <Select value={newNote.kind} onChange={(e) => setNewNote((n) => ({ ...n, kind: e.target.value }))} className="w-40">
                    {NOTE_KINDS.map((kind) => <option key={kind.value} value={kind.value}>{kind.label}</option>)}
                  </Select>
                  <Button variant="outline" onClick={() => void addNote()}>Añadir al seguimiento</Button>
                </div>
                <Textarea value={newNote.body} onChange={(e) => setNewNote((n) => ({ ...n, body: e.target.value }))} rows={2} placeholder="Qué se habló, qué queda pendiente…" />
              </div>
            ) : null}
            <div className="space-y-2">
              {(detailQuery.data?.notes_history ?? []).map((note) => (
                <div key={note.id} className="rounded-lg border border-border px-3 py-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-[11px] text-muted-foreground">
                      <span className="font-medium text-foreground">{NOTE_KINDS.find((kind) => kind.value === note.kind)?.label ?? note.kind}</span>
                      {' · '}{formatDate(note.created_at)}{note.author_name ? ` · ${note.author_name}` : ''}
                    </p>
                    {can('orders.write') ? (
                      <Button variant="ghost" size="icon-sm" title="Borrar nota" onClick={() => void removeNote(note.id)}><Trash2 /></Button>
                    ) : null}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-xs">{note.body}</p>
                </div>
              ))}
              {(detailQuery.data?.notes_history ?? []).length === 0 ? (
                <p className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-[11px] text-muted-foreground">Sin anotaciones todavía.</p>
              ) : null}
            </div>
          </section>
        ) : null}
      </div>
    </InlineView>
  );
}
